/**
 * PostgreSQL JSON-path extraction from cursor context.
 *
 * Given the buffer text and a cursor offset inside a comparison RHS string
 * literal (`WHERE metadata->>'status' = '|'`), detects whether the LHS of
 * the comparison is a PG JSON-extract expression and returns the normalized
 * canonical JSONPath (`$.status`) so the enum-value resolver can look up
 * path-keyed hints.
 *
 * Supported PG JSON path forms (v1):
 *   col->>'key'              → canonical `$.key`
 *   col->'a'->>'b'           → canonical `$.a.b`
 *   col->>'$.a.b'            → canonical `$.a.b`  (user-typed canonical form)
 *   col#>>'{a,b}'            → canonical `$.a.b`  (PG path-array text extract)
 *   col->'items'->2->>'name' → canonical `$.items[2].name`
 *
 * Intentionally NOT supported in v1 (follow-up slices):
 *   - jsonb_path_query / jsonb_path_exists function forms
 *   - recursive descent (`..`)
 *   - filter expressions inside path strings
 *
 * Pure text scanner — no tree-sitter dependency. Mirrors the backward-scan
 * pattern of `detectComparisonValueContext` and `comparisonScanHelpers`.
 *
 * This entry point owns the public API + the comparison-operator boundary
 * scan. The extract-expression parser and the string/identifier scan helpers
 * it depends on live in the sibling `extractJsonPathFromCursorHelpers.pgsql.ts`
 * (§9.2 Priority-5 split — this file was 311 lines, over the ~200-line soft
 * guide; pure mechanical move, no behavior change).
 */

import {
  skipValueOperand,
  COMPARISON_OPERATORS,
  endsWithAt,
} from '../context/comparisonScanHelpers';
import { parseExtractExprAt } from './extractJsonPathFromCursorHelpers.pgsql';

export type ExtractJsonPathResult = {
  /** Already passed through normalizeJsonPath. */
  readonly canonicalPath: string;
  /** The column name (unqualified, case-preserved) from the LHS. */
  readonly column: string;
};

/**
 * Returns the normalized JSON path when the cursor sits at the comparison
 * RHS of a PG JSON-extract expression, or `null` otherwise.
 *
 * `column` is the column name (lowercase-folded) from the resolved table
 * column binding — used to verify the extract expression targets the same
 * column so we don't offer completions for the wrong column.
 */
export function extractJsonPathFromCursorPgsql(args: {
  readonly text: string;
  readonly cursorOffset: number;
  /** Omit to accept whichever column the extract expression names — the
   *  scan itself never depends on it (the JSON-chain enum fallback scans
   *  once, then matches the returned `column` against the snapshot). */
  readonly column?: string;
}): ExtractJsonPathResult | null {
  const { text, cursorOffset, column } = args;
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Walk backward from the cursor past the in-progress string literal to land
  // on the comparison operator boundary (exclusive end of LHS).
  const opBoundary = findComparisonOpEndBefore(text, cursorOffset);
  if (opBoundary === null) return null;

  // The token immediately before the comparison operator should be the closing
  // `'` of a PG JSON-extract expression. Skip whitespace.
  let i = opBoundary - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0 || text[i] !== "'") return null;

  // Parse the extract expression ending here.
  const extracted = parseExtractExprAt(text, i);
  if (!extracted) return null;

  // Verify the column matches (case-insensitive).
  if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;

  return {
    canonicalPath: extracted.canonicalPath,
    column: extracted.column,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison operator boundary scan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back from `offset` over the optional in-progress RHS string literal,
 * using `skipValueOperand` (which handles all the SQL string-scanning edge
 * cases) and return the exclusive-end index of the LHS expression
 * (i.e., `opBoundary` where `text.slice(0, opBoundary)` ends with the
 * comparison operator). Returns `null` when no recognized comparison operator
 * precedes the cursor's operand position.
 *
 * `skipValueOperand` returns the index of the last char before the operand;
 * that char is either the end of a comparison operator or a keyword tail.
 * We look for symbolic comparison operators only (LIKE/ILIKE don't gate
 * JSON-path enum completions since they're range ops, not equality).
 */
function findComparisonOpEndBefore(text: string, offset: number): number | null {
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return null;

  // `i` is the index of the last char of the operator (exclusive end = i+1).
  const opEnd = i + 1;
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      return opEnd - op.length; // index of first char of the LHS (exclusive end)
    }
  }
  return null;
}

// Test-only exports.
export { parsePgArrayLiteral as _parsePgArrayLiteral } from './extractJsonPathFromCursorHelpers.pgsql';
